import { v4 as uuid } from 'uuid';

import {
  DatabasesAction,
  databasesStardogRequestDispatchers,
} from 'src/actions/databases/actionCreators';
import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import { PublishProjectResponse } from 'src/actions/projects/actionCreators';
import { StoredQuery } from 'src/constants/storedQuery';
import { CompetencyQuestion } from 'src/constants/voicebox';
import {
  ModelAliasInfo,
  ModelsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { Connection } from 'src/store/connections/types';
import { Database } from 'src/store/state/databases';
import { Project } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { parseStoredQueriesResults } from 'src/utils/jsonld/parseStoredQuery';
import { replaceInvalidNameChars } from 'src/utils/removeInvalidNameChars';

export const publishQuestions = async ({
  competencyQuestions,
  connection,
  database,
  modelsInfo,
  modelAliasInfo,
  project,
}: {
  competencyQuestions: CompetencyQuestion[] | undefined;
  connection: Connection | null;
  database: Database;
  modelAliasInfo?: ModelAliasInfo;
  modelsInfo: ModelsInfo;
  project: Project;
}): Promise<PublishProjectResponse[]> => {
  if (!competencyQuestions?.length) {
    return [];
  }

  const modelInfo = project.targetModelId
    ? modelsInfo[project.targetModelId] || Object.values(modelsInfo)[0]
    : Object.values(modelsInfo)[0];
  const modelName = modelAliasInfo?.modelName || modelInfo.modelName;

  const existingQuestions: Record<string, StoredQuery | undefined> = {};
  // Map of lowercase voicebox question text -> set of query names that use it.
  // Scoped to the target database + model so voicebox spotlight questions
  // outside the current publish scope don't trigger false-positive duplicates.
  const existingVoiceboxQuestions = new Map<string, Set<string>>();
  const listResponse =
    await databasesStardogRequestDispatchers.listStoredQueries();

  if (listResponse.type === DatabasesActionType.LIST_STORED_QUERIES_SUCCESS) {
    const storedQueries = parseStoredQueriesResults(
      listResponse.payload.response?.body
    );
    Object.values(storedQueries).forEach((storedQuery) => {
      if (storedQuery.competencyQuestionId) {
        existingQuestions[storedQuery.competencyQuestionId] = storedQuery;
      }
      const isInPublishScope =
        (storedQuery.database === database.id ||
          storedQuery.database === '*') &&
        storedQuery.model === modelName;
      if (storedQuery.voiceboxQuestion && isInPublishScope) {
        const key = storedQuery.voiceboxQuestion.toLowerCase();
        const existing = existingVoiceboxQuestions.get(key);
        if (existing) {
          existing.add(storedQuery.name);
        } else {
          existingVoiceboxQuestions.set(key, new Set([storedQuery.name]));
        }
      }
    });
  }

  const responses = await Promise.all(
    competencyQuestions.map((question) =>
      publishQuestion({
        databaseId: database.id,
        connection,
        question,
        modelName,
        project,
        existingQuestions,
        existingVoiceboxQuestions,
      })
    )
  );

  if (responses.every((response) => response.success)) {
    return [
      {
        success: true,
        action: responses[0].action,
        message:
          copy.notifications.publishProject.competencyQuestions.successAll,
      },
    ];
  }

  return responses;
};

export const publishQuestion = async ({
  connection,
  databaseId,
  existingQuestions,
  existingVoiceboxQuestions,
  modelName,
  project,
  question,
}: {
  databaseId: string;
  connection: Connection | null;
  existingQuestions: Record<string, StoredQuery | undefined>;
  existingVoiceboxQuestions: Map<string, Set<string>>;
  modelName: string;
  project: Project;
  question: CompetencyQuestion;
}): Promise<PublishProjectResponse> => {
  const existingQuestion = existingQuestions[question.id];
  const queryName =
    existingQuestion?.name ||
    replaceInvalidNameChars(`${project.name}__${uuid()}`);

  // Skip publishing entirely if the voicebox question already exists on another query
  const questionKey = question.question.toLowerCase();
  const existingVoiceboxQuestionName =
    existingVoiceboxQuestions.get(questionKey);
  const isDuplicateVoiceboxQuestion =
    existingVoiceboxQuestionName &&
    !(
      existingVoiceboxQuestionName.size === 1 &&
      existingVoiceboxQuestionName.has(queryName)
    );

  if (isDuplicateVoiceboxQuestion) {
    return {
      success: false,
      action: {
        type: DatabasesActionType.CREATE_STORED_QUERY_FAILURE,
        payload: { args: [], action: {} },
      },
      actionDescription:
        copy.notifications.publishProject.competencyQuestions.actionDescription,
      message:
        copy.notifications.publishProject.competencyQuestions.duplicateVoiceboxQuestion(
          question.question
        ),
    };
  }

  const storedQuery: StoredQuery = {
    creator: existingQuestion?.creator || connection?.username,
    database: databaseId,
    model: modelName,
    name: queryName,
    query: question.evaluation?.query || '',
    reasoning: true,
    shared: true,
    competencyQuestionId: question.id,
    voiceboxQuestion: question.question,
  };

  let response: DatabasesAction;
  if (existingQuestion) {
    response = await databasesStardogRequestDispatchers.updateStoredQuery(
      storedQuery
    );

    if (response.type === DatabasesActionType.UPDATE_STORED_QUERY_FAILURE) {
      return {
        success: false,
        action: response,
        actionDescription:
          copy.notifications.publishProject.competencyQuestions
            .actionDescription,
        message: copy.notifications.publishProject.competencyQuestions.failure(
          question.question
        ),
      };
    }
  } else {
    response = await databasesStardogRequestDispatchers.createStoredQuery(
      storedQuery
    );

    if (response.type === DatabasesActionType.CREATE_STORED_QUERY_FAILURE) {
      return {
        success: false,
        action: response,
        actionDescription:
          copy.notifications.publishProject.competencyQuestions
            .actionDescription,
        message: copy.notifications.publishProject.competencyQuestions.failure(
          question.question
        ),
      };
    }
  }

  return {
    success: true,
    action: response,
    message: copy.notifications.publishProject.competencyQuestions.success(
      question.question
    ),
  };
};
